/**
 * The copy of a project that an inverse action is generated from.
 *
 * The inverse generator only reads this copy, so leaves that cannot be
 * mutated through it can be shared rather than duplicated. That is what
 * lets it keep a media item's Blob and a waveform's Float32Array intact,
 * which a JSON round trip destroyed: undoing a media removal used to restore
 * an item whose file was an empty object.
 *
 * It is not structuredClone. structuredClone throws on a function, and a
 * project can carry one: test fixtures and mocks put them on media items,
 * and a snapshot taken before the executor's try block turned that from a
 * lost file into every action on the project rejecting. Functions are
 * dropped here, as the JSON copy always dropped them, and no other value in
 * a project needs anything beyond a reference or a plain recursive copy.
 */
export function snapshotForInverse<T>(value: T): T {
  return clone(value) as T;
}

function clone(value: unknown): unknown {
  if (value === null || typeof value !== "object") {
    return typeof value === "function" ? undefined : value;
  }
  if (Array.isArray(value)) {
    return value.map(clone);
  }
  if (
    ArrayBuffer.isView(value) ||
    value instanceof ArrayBuffer ||
    value instanceof Blob ||
    value instanceof Date ||
    value instanceof Map ||
    value instanceof Set ||
    value instanceof RegExp
  ) {
    return value;
  }
  const out: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    if (typeof entry === "function") continue;
    out[key] = clone(entry);
  }
  return out;
}
